import { motion, useMotionValue, useSpring } from 'framer-motion';
import { useEffect, useRef } from 'react';

export type AnimatedNumberProps = {
  value: number;
  /**
   * Function to format the number before it is displayed.
   */
  formatter?: (current: number) => string;
  /**
   * Animation duration in ms.
   */
  duration?: number;
};

/**
 * Animates a number value when it is changed.
 */
export const AnimatedNumber = ({
  value,
  formatter = current => current.toLocaleString(),
  duration = 250
}: AnimatedNumberProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { duration });

  useEffect(() => {
    motionValue.set(value);
  }, [motionValue, value]);

  useEffect(() => {
    const onSpringValueChange = (latest: number) => {
      if (!ref.current) return;

      ref.current.innerText = formatter(latest);
    };

    const unsubscribeSpringValueChange = springValue.on('change', onSpringValueChange);

    return () => {
      unsubscribeSpringValueChange();
    };
  }, [formatter, springValue]);

  return <motion.span ref={ref} />;
};
